import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/services/auth/auth.service';
import Swal from 'app/shared/utils/swal.util';
import { ConfiguracoesComponent } from '../../main-layout/configuracoes/configuracoes.component';
import { MeuPerfilComponent } from '../../main-layout/meu-perfil/meu-perfil.component';
import { PerfilUsuarioService } from '../../main-layout/meu-perfil/perfil-usuario.service';
import { ThemeSettingsPanelComponent } from '../../main-layout/theme-settings-panel/theme-settings-panel.component';
import { TrocarClienteComponent } from '../../main-layout/trocar-cliente/trocar-cliente.component';

const ROTA_LOGIN = '/authentication/signin';
const NOME_PADRAO = 'Portal Institucional';

@Component({
  selector: 'app-institucional-header',
  standalone: false,
  templateUrl: './institucional-header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../../main-layout/main-header/main-header.component.css'
})
export class InstitucionalHeaderComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly perfilUsuarioService = inject(PerfilUsuarioService);

  nome = '';
  nomeSocial = '';
  email = '';
  perfil = '';
  nomeFantasia = NOME_PADRAO;
  previewImage: string | null = null;
  exibirPerfil = false;

  ngOnInit(): void {
    this.acompanharPerfil();
    this.acompanharOrgao();
  }

  get nomeExibido(): string {
    return this.nomeSocial || this.nome;
  }

  alternarPerfil(): void {
    this.exibirPerfil = !this.exibirPerfil;
  }

  iniciais(nome: string | null | undefined): string {
    if (!nome) {
      return '';
    }

    const partes = nome.trim().split(' ').filter((parte) => parte.length > 0);

    if (partes.length === 1) {
      return partes[0].substring(0, 2).toUpperCase();
    }

    return partes
      .slice(0, 2)
      .map((parte) => parte[0].toUpperCase())
      .join('');
  }

  abrirPainelTema(): void {
    this.dialog.open(ThemeSettingsPanelComponent, {
      position: { top: '0', right: '0' },
      width: '380px',
      maxWidth: '100vw',
      height: '100vh',
      autoFocus: false,
      panelClass: 'theme-settings-dialog'
    });
  }

  meuPerfil(): void {
    this.exibirPerfil = false;

    this.dialog.open(MeuPerfilComponent, {
      data: { data: {}, action: 'Meu Perfil' },
      disableClose: true,
      panelClass: 'dialog-rounded',
      width: '1080px',
      maxWidth: '95vw',
      maxHeight: '100vh'
    }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resultado) => {
        if (resultado?.success) {
          Swal.fire('Sucesso', resultado.message || 'Operação realizada com sucesso!', 'success');
        }
      });
  }

  configuracao(): void {
    this.exibirPerfil = false;

    this.dialog.open(ConfiguracoesComponent, {
      data: { data: {}, action: 'Configurações' },
      disableClose: true,
      panelClass: 'dialog-rounded',
      maxWidth: 'auto',
      maxHeight: '100vh'
    });
  }

  trocarCliente(): void {
    this.exibirPerfil = false;

    this.dialog.open(TrocarClienteComponent, {
      data: { data: {}, action: 'Trocar Cliente' },
      disableClose: true,
      panelClass: 'dialog-rounded',
      width: '1080px',
      maxWidth: '95vw',
      maxHeight: '100vh'
    }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resultado) => {
        if (!resultado?.success) {
          return;
        }

        const urlAtual = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigateByUrl(urlAtual);
        });
      });
  }

  sair(): void {
    this.exibirPerfil = false;
    this.authService.logout();
    this.router.navigateByUrl(ROTA_LOGIN);
  }

  private acompanharPerfil(): void {
    this.perfilUsuarioService.perfil$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((perfil) => {
        this.nome = perfil.nome;
        this.nomeSocial = perfil.nomeSocial ?? '';
        this.email = perfil.email;
        this.perfil = perfil.perfil;
        this.previewImage = perfil.midia;
        this.changeDetectorRef.markForCheck();
      });
  }

  private acompanharOrgao(): void {
    this.authService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((usuario) => {
        this.nomeFantasia = usuario?.orgao?.trim() || NOME_PADRAO;
        this.changeDetectorRef.markForCheck();
      });
  }
}
